import React from 'react';
import { formatOMR } from '../../utils/formatCurrency';

const COMPANY_NAME = 'Electronic City for IT Services';

const TicketPrintTemplate = ({ ticket, members }) => {
  if (!ticket) return null;

  const salesMembers = (members || [])?.filter(m => m?.memberType === 'SALES');
  const projectMembers = (members || [])?.filter(m => m?.memberType === 'PROJECT_TEAM');

  const issueDate = ticket?.createdAt
    ? new Date(ticket.createdAt)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })
    : new Date()?.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });

  // Build timeline from ticket.approvals (which has performer.full_name)
  // Fall back to individual fields only if approvals not available
  const buildTimeline = () => {
    const approvals = ticket?.approvals;
    if (approvals && approvals?.length > 0) {
      // Map approval actions to display labels
      const actionLabelMap = {
        'Draft': 'Created',
        'Pending Team Assignment': 'Created',
        'Calculated': 'Calculated',
        'Pending Approval': 'Submitted',
        'Approved': 'Approved',
        'Ready for Payment': 'Finance Reviewed',
        'Paid': 'Paid',
        'Rejected': 'Rejected',
        'On Hold': 'On Hold',
      };

      // Sort ascending by created_at
      const sorted = [...approvals]?.sort((a, b) => new Date(a?.created_at) - new Date(b?.created_at));

      return sorted?.filter(a => a?.performed_by && a?.created_at)?.map(a => ({
          label: actionLabelMap?.[a?.to_status] || a?.action || a?.to_status || 'Action',
          by: a?.performer?.full_name || a?.performer?.email || a?.performed_by || '—',
          at: a?.created_at,
        }));
    }

    // Fallback: use individual ticket fields with creator/approver joins
    const rows = [
      { label: 'Created', by: ticket?.creator?.full_name || ticket?.creator?.email || ticket?.createdBy, at: ticket?.createdAt },
      { label: 'Calculated', by: ticket?.calculatedBy, at: ticket?.calculatedAt },
      { label: 'Submitted', by: ticket?.submittedBy, at: ticket?.submittedAt },
      { label: 'Approved', by: ticket?.approver?.full_name || ticket?.approver?.email || ticket?.approvedBy, at: ticket?.approvedAt },
      { label: 'Finance Reviewed', by: ticket?.financeReviewedBy, at: ticket?.financeReviewedAt },
      { label: 'Paid', by: ticket?.paidBy, at: ticket?.paidAt },
    ];

    return rows?.filter(t => t?.at && t?.by && !isUUID(t?.by));
  };

  // Helper: detect UUID strings so we never show them
  const isUUID = (str) => {
    if (!str || typeof str !== 'string') return false;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i?.test(str?.trim());
  };

  const timeline = buildTimeline();

  return (
    <>
      {/* Suppress browser print headers/footers (URL, date, page number, title) */}
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 10mm 12mm 10mm 12mm;
          }
          html, body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          /* Hide everything except the print area */
          body > *:not(#ticket-print-root) {
            display: none !important;
          }
        }
      `}</style>

      <div id="ticket-print-area" style={{ fontFamily: 'Arial, sans-serif', color: '#111', background: 'white', padding: '0', display: 'none', minHeight: '257mm', flexDirection: 'column' }}>

        {/* ── Header: Logo left | Document info right ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '3px solid #1a3a5c', paddingBottom: '12px', marginBottom: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img
              src="/assets/images/logo_PNG_RGB-1782114652250.png"
              alt="Electronic City for IT Services Logo"
              style={{ height: '48px', width: 'auto', objectFit: 'contain', display: 'block' }}
            />
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '13pt', fontWeight: 'bold', color: '#1a3a5c', textTransform: 'uppercase', letterSpacing: '1px' }}>Project Incentive Ticket</div>
            <div style={{ fontSize: '9pt', color: '#374151', marginTop: '6px', lineHeight: '1.6' }}>
              <div><span style={{ color: '#6b7280' }}>Ticket No:</span> <strong>{ticket?.ticketNumber || '—'}</strong></div>
              <div><span style={{ color: '#6b7280' }}>Status:</span> <strong>{ticket?.status || '—'}</strong></div>
              <div><span style={{ color: '#6b7280' }}>Issue Date:</span> <strong>{issueDate}</strong></div>
            </div>
          </div>
        </div>

        {/* ── Ticket & Status Info ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
          <div style={{ padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
            <div style={{ fontSize: '9pt', fontWeight: 'bold', color: '#1a3a5c', marginBottom: '8px', textTransform: 'uppercase' }}>Project Information</div>
            {[
              { label: 'Ticket Number', value: ticket?.ticketNumber },
              { label: 'Project Name', value: ticket?.projectName },
              { label: 'PTC Number', value: ticket?.clientName || '—' },
              { label: 'PO / LPO Reference', value: ticket?.clientPoReference || '—' },
              { label: 'Deal Won Date', value: ticket?.wonDate ? new Date(ticket?.wonDate)?.toLocaleDateString('en-GB') : '—' },
            ]?.map((f, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '9pt' }}>
                <span style={{ color: '#6b7280' }}>{f?.label}:</span>
                <span style={{ fontWeight: 'bold' }}>{f?.value}</span>
              </div>
            ))}
          </div>
          <div style={{ padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
            <div style={{ fontSize: '9pt', fontWeight: 'bold', color: '#1a3a5c', marginBottom: '8px', textTransform: 'uppercase' }}>Status Information</div>
            {[
              { label: 'Ticket Status', value: ticket?.status },
              { label: 'Deal Status', value: ticket?.dealStatus || 'Open' },
              { label: 'Sales Deal Owner', value: ticket?.salesDealOwnerName || '—' },
              { label: 'Payment Reference', value: ticket?.paymentReference || '—' },
            ]?.map((f, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '9pt' }}>
                <span style={{ color: '#6b7280' }}>{f?.label}:</span>
                <span style={{ fontWeight: 'bold' }}>{f?.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Financial Summary ── */}
        <div style={{ marginBottom: '14px', padding: '10px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px' }}>
          <div style={{ fontSize: '10pt', fontWeight: 'bold', color: '#1a3a5c', marginBottom: '8px', textTransform: 'uppercase' }}>Financial Summary</div>
          <table style={{ width: '100%' }}>
            <tbody>
              <tr>
                {[
                  { label: 'Final Project Value', value: formatOMR(ticket?.finalProjectValue || ticket?.projectValue) },
                  { label: 'Total Cost', value: formatOMR(ticket?.totalCost) },
                  { label: 'Net Profit / Target Margin', value: formatOMR(ticket?.finalTargetMarginAmount || ticket?.targetMarginAmount) },
                  { label: 'Sales Incentive (2%)', value: formatOMR(ticket?.salesIncentiveAmount) },
                  { label: 'Project Team Incentive (2%)', value: formatOMR(ticket?.projectTeamIncentiveAmount) },
                  { label: 'Total Incentives', value: formatOMR(ticket?.totalIncentives) },
                  { label: 'Profit After Incentives', value: formatOMR(ticket?.profitAfterIncentives) },
                ]?.map((f, i) => (
                  <td key={i} style={{ textAlign: 'center', padding: '6px 8px', border: '1px solid #d1fae5', background: 'white', borderRadius: '4px' }}>
                    <div style={{ fontSize: '8pt', color: '#6b7280' }}>{f?.label}</div>
                    <div style={{ fontSize: '10pt', fontWeight: 'bold', color: '#1a3a5c' }}>{f?.value}</div>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        {/* ── Incentive Members Table ── */}
        <div style={{ marginBottom: '14px' }}>
          <div style={{ fontSize: '10pt', fontWeight: 'bold', color: '#1a3a5c', marginBottom: '8px', textTransform: 'uppercase' }}>Incentive Members</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#1a3a5c' }}>
                {['No.', 'Employee Name', 'Department', 'Type', 'Share %', 'Incentive Amount', 'Payment Status']?.map(h => (
                  <th key={h} style={{ background: '#1a3a5c', color: 'white', padding: '7px 8px', textAlign: 'left', fontSize: '9pt', fontWeight: 'bold', borderRight: '1px solid #2d5a8e' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...salesMembers, ...projectMembers]?.map((m, idx) => (
                <tr key={idx} style={{ background: idx % 2 === 0 ? 'white' : '#f8fafc' }}>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb' }}>{idx + 1}</td>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb', fontWeight: 'bold' }}>{m?.employeeName}</td>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb' }}>{m?.department || '—'}</td>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb', fontWeight: 'bold', color: m?.memberType === 'SALES' ? '#1d4ed8' : '#7c3aed' }}>{m?.memberType === 'SALES' ? 'Sales' : 'Project Team'}</td>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb', textAlign: 'right' }}>{(parseFloat(m?.sharePercentage) || 0)?.toFixed(2)}%</td>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 'bold' }}>{formatOMR(m?.incentiveAmount)}</td>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb' }}>{m?.paymentStatus}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: '#1a3a5c' }}>
                <td colSpan={5} style={{ padding: '7px 8px', fontSize: '9pt', fontWeight: 'bold', color: 'white' }}>TOTAL INCENTIVES</td>
                <td style={{ padding: '7px 8px', fontSize: '10pt', fontWeight: 'bold', color: 'white', textAlign: 'right' }}>{formatOMR(ticket?.totalIncentives)}</td>
                <td style={{ padding: '7px 8px', color: 'white' }}></td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* ── Approval Timeline ── */}
        {timeline?.length > 0 && (
          <div style={{ marginBottom: '14px', padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
            <div style={{ fontSize: '10pt', fontWeight: 'bold', color: '#1a3a5c', marginBottom: '8px', textTransform: 'uppercase' }}>Approval Timeline</div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#e2e8f0' }}>
                  {['Action', 'Actioned By', 'Date']?.map(h => (
                    <th key={h} style={{ padding: '5px 8px', textAlign: 'left', fontSize: '9pt', fontWeight: 'bold', color: '#374151' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {timeline?.map((t, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc' }}>
                    <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb', fontWeight: 'bold', color: '#1a3a5c' }}>{t?.label}</td>
                    <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb', fontWeight: '500' }}>{t?.by || '—'}</td>
                    <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb' }}>{t?.at ? new Date(t.at)?.toLocaleDateString('en-GB') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Spacer pushes signature section to the bottom ── */}
        <div style={{ flex: 1 }} />

        {/* ── Signature Section — pinned at the bottom ── */}
        <div style={{ borderTop: '2px solid #1a3a5c', paddingTop: '12px', marginTop: '24px' }}>
          <table style={{ width: '100%' }}>
            <tbody>
              <tr>
                {[
                  { label: 'Manager Signature' },
                  { label: 'Finance Signature' },
                  { label: 'Company Stamp' },
                ]?.map((sig, i) => (
                  <td key={i} style={{ width: '33%', paddingRight: '20px', verticalAlign: 'top' }}>
                    <div style={{ borderTop: '1px solid #374151', paddingTop: '6px', marginTop: '32px' }}>
                      <div style={{ fontSize: '9pt', fontWeight: 'bold', color: '#1a3a5c' }}>{sig?.label}</div>
                      <div style={{ fontSize: '8pt', color: '#9ca3af', marginTop: '2px' }}>Name & Date</div>
                    </div>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

      </div>
    </>
  );
};

export default TicketPrintTemplate;
